import { useDispatch, useSelector, useStore } from 'react-redux';
import { setProcessedJson, setViewerLoading, setViewer } from '../store/actions';
import { generateBPMNXML } from '../services/generateBPMNXML';
import { getAbortController } from '../utils/abortController';

/**
 * Custom hook to handle rendering of BPMN diagrams (from JSON or XML).
 * Handles async rendering, abort logic, and toggles the assistant loader.
 * 
 * @returns {Function} A function to trigger rendering of a BPMN diagram.
 */
export function useBPMNRenderer() {
  const dispatch = useDispatch();
  const store = useStore();
  const viewer = useSelector((state) => state.viewer);

  /**
   * Async function to render BPMN diagram from provided JSON/XML data or history.
   * Handles loader, abort, and errors.
   * 
   * @param {string} jsonData - JSON data to generate BPMN XML from.
   * @param {string} xmlData - Direct BPMN XML to render.
   */
  const renderBPMN = async ({ jsonData, xmlData } = {}) => {
    const abortController = getAbortController();
    const { history } = store.getState();

    if (!viewer) {
      console.error("BPMN viewer is not initialized yet!");
      return;
    }

    let effectiveJsonData = jsonData;
    let effectiveXmlData = xmlData;

    if (typeof effectiveJsonData === "undefined" && typeof effectiveXmlData === "undefined") {
      // Render from history if nothing provided
      effectiveJsonData = history.entries[history.pointer]?.jsonData;
    }

    if (typeof effectiveJsonData === "undefined" && typeof effectiveXmlData === "undefined") {
      console.warn('No valid data to render BPMN');
      return;
    }

    dispatch(setViewerLoading(true));

    try {
      let bpmnXML = effectiveXmlData;
      let processedJson = null;
      if (!bpmnXML) {
        // If no XML, generate from JSON
        const result = await generateBPMNXML(
          typeof effectiveJsonData === "string" ? JSON.parse(effectiveJsonData) : effectiveJsonData,
          abortController.signal
        );

        // Abort-safety after the async operation
        if (abortController.signal.aborted) {
          console.warn("Operation aborted while generating BPMN XML.");
          return;
        }
        processedJson = result.processedJson;
        bpmnXML = result.bpmnXML;
      }

      await viewer.importXML(bpmnXML);
      dispatch(setViewer(viewer));
      dispatch(setProcessedJson(processedJson));
      if (viewer.get('canvas')) {
        viewer.get('canvas').zoom('fit-viewport'); // Fit current BPMN diagram XML to viewport
      }
    } catch (error) {
      if (
        !(
          (error && error.name === 'AbortError') ||
          (error && error.code === 'ERR_CANCELED')
        )
      ) {
        console.error(`There was an error translating LLM response to BPMN or rendering: ${error}`);
      }
      throw error;
    } finally {
      dispatch(setViewerLoading(false));
    }
  };

  return renderBPMN;
}
